/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, Typography, Grid, Box } from '@mui/material';
import braintree from 'braintree-web';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventIcon from '@mui/icons-material/Event';
import NumbersIcon from '@mui/icons-material/Numbers';
import { useFormikContext } from 'formik';
import { PaymentMethodForm } from 'renderer/features/settings/schemas/paymentMethods.schemas';

interface PaymentMethodProps {
  clientToken: string;
  hostedFieldsInstance: braintree.HostedFields | null;
  setHostedFieldsInstance: (hostedFields: braintree.HostedFields) => void;
  disabled: boolean;
}

const PaymentMethodFormComponent: React.FC<PaymentMethodProps> = ({
  clientToken,
  hostedFieldsInstance,
  setHostedFieldsInstance,
  disabled,
}) => {
  const { setFieldError, setFieldValue, setFieldTouched, touched, errors } =
    useFormikContext<PaymentMethodForm>();
  const cardNumberRef = useRef<HTMLDivElement>(null);
  const expiryRef = useRef<HTMLDivElement>(null);
  const cvvRef = useRef<HTMLDivElement>(null);

  const [cardValidity, setCardValidity] = useState(false);
  const [expiryValidity, setExpiryValidity] = useState(false);
  const [cvvValidity, setCvvValidity] = useState(false);

  const [cardInteracted, setCardInteracted] = useState(false);
  const [expiryInteracted, setExpiryInteracted] = useState(false);
  const [cvvInteracted, setCvvInteracted] = useState(false);

  const getBorderStyle = (
    isValid: boolean | undefined,
    isInteracted: boolean | undefined,
    submitError: boolean,
  ): string => {
    if ((isInteracted || submitError) && !disabled) {
      // Only change border color if the field was interacted with
      return isValid ? '1px solid grey' : '1px solid red';
    }
    return '1px solid grey'; // Default state for untouched fields
  };

  // useEffect(() => {
  //   if (touched.newPaymentMethodToken) {
  //     setCardInteracted(true);
  //     setExpiryInteracted(true);
  //     setCvvInteracted(true);
  //   }
  // }, [touched.newPaymentMethodToken]);

  useEffect(() => {
    if (hostedFieldsInstance) {
      if (disabled) {
        hostedFieldsInstance.setAttribute({
          field: 'number',
          attribute: 'disabled',
          value: 'true',
        });
        hostedFieldsInstance.setAttribute({
          field: 'expirationDate',
          attribute: 'disabled',
          value: 'true',
        });
        hostedFieldsInstance.setAttribute({
          field: 'cvv',
          attribute: 'disabled',
          value: 'true',
        });
      } else {
        hostedFieldsInstance.removeAttribute({
          field: 'number',
          attribute: 'disabled',
        });
        hostedFieldsInstance.removeAttribute({
          field: 'expirationDate',
          attribute: 'disabled',
        });
        hostedFieldsInstance.removeAttribute({
          field: 'cvv',
          attribute: 'disabled',
        });
      }
    }
  }, [disabled, hostedFieldsInstance]);

  useEffect(() => {
    const setupBraintree = async () => {
      try {
        const clientInstance = await braintree.client.create({
          authorization: clientToken,
        });

        // Ensure all refs are not null
        if (!cardNumberRef.current || !expiryRef.current || !cvvRef.current) {
          setFieldError(
            'newPaymentMethodToken',
            'Could not set up payment form',
          );
          // console.error('Braintree setup error: Refs are null');
          return;
        }

        const hostedFields = await braintree.hostedFields.create({
          client: clientInstance,
          styles: {
            input: {
              'font-size': '14px',
              'font-family': 'Arial, sans-serif',
              color: '#3f3f3f',
              padding: '10px',
              height: '34px', // Adjust height as needed
            },
            // Additional styles can go here
          },
          fields: {
            number: {
              container: cardNumberRef.current,
              placeholder: '1234 1234 1234 1234',
            },
            expirationDate: {
              container: expiryRef.current,
              placeholder: 'MM / YY',
            },
            cvv: {
              container: cvvRef.current,
              placeholder: '123',
            },
          },
        });

        hostedFields.on('focus', (event) => {
          console.log(event.emittedBy, 'focus', event);
          const { fields } = event;
          switch (event.emittedBy) {
            case 'number':
              setCardInteracted(true);
              setCardValidity(
                fields.number.isPotentiallyValid, // || fields.number.isValid,
              );
              break;
            case 'expirationDate':
              setExpiryInteracted(true);
              setExpiryValidity(
                fields.expirationDate.isPotentiallyValid, // || fields.number.isValid,
              );
              break;
            case 'cvv':
              setCvvInteracted(true);
              setCvvValidity(
                fields.cvv.isPotentiallyValid, // || fields.number.isValid,
              );
              break;
            default:
              break;
          }
        });

        hostedFields.on('blur', (event) => {
          console.log(event.emittedBy, 'blur', event);
          const { fields } = event;
          switch (event.emittedBy) {
            case 'number':
              setCardInteracted(true);
              setCardValidity(
                fields.number.isValid, // || fields.number.isPotentiallyValid,
              );
              break;
            case 'expirationDate':
              setExpiryInteracted(true);
              setExpiryValidity(
                fields.expirationDate.isValid, // || fields.number.isValid,
              );
              break;
            case 'cvv':
              setCvvInteracted(true);
              setCvvValidity(
                fields.cvv.isValid, // || fields.number.isValid,
              );
              break;
            default:
              break;
          }
        });

        hostedFields.on('validityChange', (event) => {
          console.log('validityChange', event);
          const { fields } = event;
          setCardValidity(fields.number.isValid);
          setExpiryValidity(fields.expirationDate.isValid);
          setCvvValidity(fields.cvv.isValid);

          setFieldValue(
            'numberValidity',
            !fields.number.isValid ? '' : 'valid',
          );
          setFieldValue(
            'expirationDateValidity',
            !fields.expirationDate.isValid ? '' : 'valid',
          );
          setFieldValue('cvvValidity', !fields.number.isValid ? '' : 'valid');

          if (!fields.number.isEmpty) setCardInteracted(true);
          if (!fields.expirationDate.isEmpty) setExpiryInteracted(true);
          if (!fields.cvv.isEmpty) setCvvInteracted(true);

          if (
            fields.number.isValid &&
            fields.expirationDate.isValid &&
            fields.cvv.isValid
          ) {
            setFieldValue('newPaymentMethodToken', 'valid');
          } else {
            setFieldValue('newPaymentMethodToken', '');
          }
        });

        setHostedFieldsInstance(hostedFields);
      } catch (error) {
        setFieldError(
          'newPaymentMethodToken',
          `Braintree setup error: ${error}`,
        );
      }
    };

    setupBraintree();

    return () => {
      // If you have set the hostedFieldsInstance, tear it down
      if (hostedFieldsInstance) {
        try {
          hostedFieldsInstance.teardown();
        } catch (teardownError) {
          setFieldError(
            'newPaymentMethodToken',
            `Braintree teardown error: ${teardownError}`,
          );
        }
        // hostedFieldsInstance.teardown().catch((teardownError: unknown) => {
        // });
      }
    };
  }, [clientToken]);

  console.log('Braintree form');
  console.log({ touched, errors });

  return (
    <form>
      <Card elevation={0}>
        <CardContent>
          <Grid container spacing={2}>
            {/* Card Number with Icon */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">Card Number:</Typography>
              <Typography variant="body2">Enter your card number</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <CreditCardIcon sx={{ marginRight: '10px' }} />
              <Box
                ref={cardNumberRef}
                sx={{
                  border: getBorderStyle(
                    cardValidity,
                    cardInteracted,
                    !!(touched.numberValidity && errors.numberValidity),
                  ),
                  borderRadius: '4px',
                  flexGrow: 1,
                  height: '35px',
                  iframe: { height: '100%' },
                }}
              />
            </Grid>

            {/* Expiry Date with Icon */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">Expiry Date:</Typography>
              <Typography variant="body2">
                Enter the expiration on the card
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <EventIcon sx={{ marginRight: '10px' }} />
              <Box
                ref={expiryRef}
                sx={{
                  border: getBorderStyle(
                    expiryValidity,
                    expiryInteracted,
                    !!(
                      touched.expirationDateValidity &&
                      errors.expirationDateValidity
                    ),
                  ),
                  borderRadius: '4px',
                  flexGrow: 1,
                  height: '35px',
                  iframe: { height: '100%' },
                }}
              />
            </Grid>

            {/* CVV Number with Icon */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">CVV Number:</Typography>
              <Typography variant="body2">
                Enter the 3 or 4 digit number
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <NumbersIcon sx={{ marginRight: '10px' }} />
              <Box
                ref={cvvRef}
                sx={{
                  border: getBorderStyle(
                    cvvValidity,
                    cvvInteracted,
                    !!(touched.cvvValidity && errors.cvvValidity),
                  ),
                  borderRadius: '4px',
                  flexGrow: 1,
                  height: '35px',
                  iframe: { height: '100%' },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  );
};

export default PaymentMethodFormComponent;
